import { TranslateModule } from '@ngx-translate/core';
import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Equipment } from 'src/app/bo/equipment';
import { Operator } from 'src/app/bo/operator';
import { DrillActivityService } from 'src/app/services/drill-activity.service';
import { DrillSessionApiDto } from 'src/app/shared/models/drilling.models';

export interface SessionFormDialogData {
  blastId: number;
  siteId: number;
  session?: DrillSessionApiDto;
  timezone?: string;
}

function utcToSiteLocal(isoStr: string | undefined, timezone?: string): string {
  if (!isoStr) return '';
  const date = new Date(isoStr);
  if (!timezone) return isoStr.replace('Z', '').substring(0, 16);
  const offsetMs = getTimezoneOffsetMs(timezone);
  const local = new Date(date.getTime() + offsetMs);
  return local.toISOString().substring(0, 16);
}

function siteLocalToUtc(localStr: string, timezone?: string): string {
  if (!timezone) return localStr + ':00.000Z';
  const offsetMs = getTimezoneOffsetMs(timezone);
  const local = new Date(localStr + ':00.000Z');
  const utc = new Date(local.getTime() - offsetMs);
  return utc.toISOString();
}

function currentSiteLocal(timezone?: string): string {
  const now = new Date();
  if (!timezone) {
    const pad = (n: number): string => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  }
  const offsetMs = getTimezoneOffsetMs(timezone);
  const local = new Date(now.getTime() + offsetMs);
  return local.toISOString().substring(0, 16);
}

function getTimezoneOffsetMs(timezone: string): number {
  const match = timezone.match(/UTC([+-])(\d{2}):(\d{2})/);
  if (!match) return 0;
  const sign = match[1] === '+' ? 1 : -1;
  return sign * (parseInt(match[2]) * 60 + parseInt(match[3])) * 60_000;
}

@Component({
  selector: 'blast-drilling-session-form-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatSelectModule, FormsModule, TranslateModule],
  template: `
    <h2 mat-dialog-title>{{ (isEdit ? 'OPERATIONS.SESSION_EDIT' : 'HOLES_TAB.DRILLING_ADD_SESSION') | translate }}</h2>

    <mat-dialog-content class="session-form-content">
      <div class="form-row">
        <mat-form-field appearance="outline" class="form-field">
          <mat-label>{{ 'OPERATIONS.SESSION_START' | translate }}</mat-label>
          <input matInput type="datetime-local" [(ngModel)]="formData.sessionStart" name="sessionStart" required>
        </mat-form-field>
        <div class="form-field-wrap">
          <mat-form-field appearance="outline" class="form-field" [class.field-error-active]="sessionEndInvalid">
            <mat-label>{{ 'OPERATIONS.SESSION_END' | translate }}</mat-label>
            <input matInput type="datetime-local" [(ngModel)]="formData.sessionEnd" name="sessionEnd" required>
          </mat-form-field>
          @if (sessionEndInvalid) {
            <div class="field-error-msg">{{ 'OPERATIONS.ERR_END_AFTER_START' | translate }}</div>
          }
        </div>
      </div>

      <div class="form-row">
        <mat-form-field appearance="outline" class="form-field">
          <mat-label>{{ 'HOLES_TAB.DRILLING_COL_RIG' | translate }}</mat-label>
          <mat-select [(ngModel)]="formData.drillRigId" name="drillRigId" required>
            @for (eq of equipments(); track eq.equipementId) {
              <mat-option [value]="eq.equipementId">{{ eq.name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="outline" class="form-field">
          <mat-label>{{ 'HOLES_TAB.DRILLING_COL_OPERATOR' | translate }}</mat-label>
          <mat-select [(ngModel)]="formData.operatorId" name="operatorId" required>
            @for (op of operators(); track op.operatorId) {
              <mat-option [value]="op.operatorId">{{ op.nom }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      </div>

      <div class="form-row">
        <mat-form-field appearance="outline" class="form-field">
          <mat-label>{{ 'OPERATIONS.ENGINE_HOURS_START' | translate }}</mat-label>
          <input matInput type="number" [(ngModel)]="formData.engineHoursStart" name="engineHoursStart" min="0">
        </mat-form-field>
        <div class="form-field-wrap">
          <mat-form-field appearance="outline" class="form-field" [class.field-error-active]="engineHoursEndInvalid">
            <mat-label>{{ 'OPERATIONS.ENGINE_HOURS_END' | translate }}</mat-label>
            <input matInput type="number" [(ngModel)]="formData.engineHoursEnd" name="engineHoursEnd" min="0">
          </mat-form-field>
          @if (engineHoursEndInvalid) {
            <div class="field-error-msg">{{ 'OPERATIONS.ERR_ENGINE_HOURS_END' | translate }}</div>
          }
        </div>
      </div>

      <div class="form-row">
        <mat-form-field appearance="outline" class="form-field">
          <mat-label>{{ 'OPERATIONS.DRIFTER_HOURS_START' | translate }}</mat-label>
          <input matInput type="number" [(ngModel)]="formData.drifterHoursStart" name="drifterHoursStart" min="0">
        </mat-form-field>
        <div class="form-field-wrap">
          <mat-form-field appearance="outline" class="form-field" [class.field-error-active]="drifterHoursEndInvalid">
            <mat-label>{{ 'OPERATIONS.DRIFTER_HOURS_END' | translate }}</mat-label>
            <input matInput type="number" [(ngModel)]="formData.drifterHoursEnd" name="drifterHoursEnd" min="0">
          </mat-form-field>
          @if (drifterHoursEndInvalid) {
            <div class="field-error-msg">{{ 'OPERATIONS.ERR_DRIFTER_HOURS_END' | translate }}</div>
          }
        </div>
      </div>

      <div class="form-row">
        <div class="form-field-wrap">
          <mat-form-field appearance="outline" class="form-field" [class.field-error-active]="pauseNotWholeMinutes || pauseMinutesInvalid">
            <mat-label>{{ 'OPERATIONS.TOTAL_PAUSE_MINUTES' | translate }}</mat-label>
            <input matInput type="number" [(ngModel)]="formData.totalPauseMinutes" name="totalPauseMinutes" min="0" step="1">
          </mat-form-field>
          @if (pauseNotWholeMinutes) {
            <div class="field-error-msg">{{ 'OPERATIONS.ERR_PAUSE_WHOLE_MINUTES' | translate }}</div>
          } @else if (pauseMinutesInvalid) {
            <div class="field-error-msg">{{ 'OPERATIONS.ERR_PAUSE_EXCEEDS' | translate: { minutes: sessionDurationMinutes } }}</div>
          }
        </div>
        <mat-form-field appearance="outline" class="form-field">
          <mat-label>{{ 'OPERATIONS.TOTAL_DRILLED_LENGTH' | translate }}</mat-label>
          <input matInput type="number" [(ngModel)]="formData.totalDrilledLength" name="totalDrilledLength" min="0">
        </mat-form-field>
      </div>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>{{ 'GLOBAL.ANNULER' | translate }}</button>
      <button mat-button color="primary" [disabled]="!isValid" (click)="onSubmit()">{{ 'GLOBAL.ENREGISTRER' | translate }}</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .session-form-content {
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 520px;
    }
    .mat-mdc-dialog-title + .mat-mdc-dialog-content.session-form-content {
      padding-top: 12px;
    }
    .form-row {
      display: flex;
      gap: 16px;
    }
    .form-field-wrap {
      flex: 1;
      display: flex;
      flex-direction: column;
    }
    .form-field {
      flex: 1;
      width: 100%;
    }
    .field-error-msg {
      color: #f44336;
      font-size: 12px;
      margin-top: -16px;
      padding-left: 16px;
      line-height: 1.4;
    }
    .field-error-active ::ng-deep .mdc-notched-outline__leading,
    .field-error-active ::ng-deep .mdc-notched-outline__notch,
    .field-error-active ::ng-deep .mdc-notched-outline__trailing {
      border-color: #f44336 !important;
    }
    .field-error-active ::ng-deep .mdc-floating-label {
      color: #f44336 !important;
    }
  `],
})
export class BlastDrillingSessionFormDialogComponent {
  private readonly drillActivityService = inject(DrillActivityService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly isEdit: boolean;
  protected readonly equipments = signal<Equipment[]>([]);
  protected readonly operators = signal<Operator[]>([]);

  protected formData: {
    sessionStart: string;
    sessionEnd: string;
    drillRigId: number | null;
    operatorId: number | null;
    engineHoursStart: number | null;
    engineHoursEnd: number | null;
    drifterHoursStart: number | null;
    drifterHoursEnd: number | null;
    /** Entered in minutes; the API stores hours. */
    totalPauseMinutes: number | null;
    totalDrilledLength: number;
  };

  /** Pause as first shown, to send the stored hours back untouched when the field is not changed. */
  private readonly initialPauseMinutes: number | null;

  constructor(
    private readonly dialogRef: MatDialogRef<BlastDrillingSessionFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private readonly data: SessionFormDialogData,
  ) {
    this.isEdit = !!data.session;
    const s = data.session;
    this.initialPauseMinutes = s?.totalPauseHours != null ? Math.round(s.totalPauseHours * 60) : null;
    this.formData = {
      sessionStart: utcToSiteLocal(s?.sessionStartUtc, data.timezone) || currentSiteLocal(data.timezone),
      sessionEnd: utcToSiteLocal(s?.sessionEndUtc, data.timezone),
      drillRigId: null,
      operatorId: null,
      engineHoursStart: s?.engineHoursStart ?? null,
      engineHoursEnd: s?.engineHoursEnd ?? null,
      drifterHoursStart: s?.drifterHoursStart ?? null,
      drifterHoursEnd: s?.drifterHoursEnd ?? null,
      totalPauseMinutes: this.initialPauseMinutes,
      totalDrilledLength: s?.totalDrilledLength ?? 0,
    };

    forkJoin([
      this.drillActivityService.fetchEquipmentBySite(data.siteId),
      this.drillActivityService.fetchOperatorsBySite(data.siteId),
    ]).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([equipList, opList]) => {
        this.equipments.set(equipList);
        this.operators.set(opList);
        // Preselect only after options exist so mat-select can match the value
        this.formData.drillRigId = s?.drillRigId ?? null;
        this.formData.operatorId = s?.operatorId ?? null;
      });
  }

  protected get sessionEndInvalid(): boolean {
    return !!this.formData.sessionEnd && !!this.formData.sessionStart &&
      new Date(this.formData.sessionEnd) <= new Date(this.formData.sessionStart);
  }

  protected get engineHoursEndInvalid(): boolean {
    return this.formData.engineHoursEnd !== null && this.formData.engineHoursStart !== null &&
      this.formData.engineHoursEnd < this.formData.engineHoursStart;
  }

  protected get drifterHoursEndInvalid(): boolean {
    return this.formData.drifterHoursEnd !== null && this.formData.drifterHoursStart !== null &&
      this.formData.drifterHoursEnd < this.formData.drifterHoursStart;
  }

  protected get sessionDurationMinutes(): number {
    if (!this.formData.sessionStart || !this.formData.sessionEnd) return 0;
    const ms = new Date(this.formData.sessionEnd).getTime() - new Date(this.formData.sessionStart).getTime();
    return ms > 0 ? Math.round(ms / 60_000) : 0;
  }

  /** `min` and `step` on the input only flag the native control: the form checks it here. */
  protected get pauseNotWholeMinutes(): boolean {
    const minutes = this.formData.totalPauseMinutes;
    return minutes !== null && minutes !== undefined && !(Number.isInteger(minutes) && minutes >= 0);
  }

  protected get pauseMinutesInvalid(): boolean {
    if (this.formData.totalPauseMinutes === null || this.formData.totalPauseMinutes === undefined) return false;
    if (!this.formData.sessionStart || !this.formData.sessionEnd) return false;
    const durationMs = new Date(this.formData.sessionEnd).getTime() - new Date(this.formData.sessionStart).getTime();
    if (durationMs <= 0) return false;
    return this.formData.totalPauseMinutes > durationMs / 60_000;
  }

  /** Minutes back to the hours the API stores; an unchanged field returns the stored value as is. */
  private get totalPauseHours(): number | undefined {
    const minutes = this.formData.totalPauseMinutes;
    if (minutes === this.initialPauseMinutes) return this.data.session?.totalPauseHours ?? undefined;
    return minutes === null || minutes === undefined ? undefined : minutes / 60;
  }

  protected get isValid(): boolean {
    return !!this.formData.sessionStart &&
      !!this.formData.sessionEnd &&
      !!this.formData.drillRigId &&
      !!this.formData.operatorId &&
      !this.sessionEndInvalid &&
      !this.engineHoursEndInvalid &&
      !this.drifterHoursEndInvalid &&
      !this.pauseNotWholeMinutes &&
      !this.pauseMinutesInvalid;
  }

  protected onSubmit(): void {
    const dto: DrillSessionApiDto = {
      id: this.data.session?.id ?? '',
      blastId: this.data.blastId,
      drillRigId: this.formData.drillRigId ?? undefined,
      drillRigName: this.equipments().find(e => e.equipementId === this.formData.drillRigId)?.name || undefined,
      operatorId: this.formData.operatorId ?? undefined,
      operatorName: this.operators().find(o => o.operatorId === this.formData.operatorId)?.nom || undefined,
      sessionStartUtc: siteLocalToUtc(this.formData.sessionStart, this.data.timezone),
      sessionEndUtc: this.formData.sessionEnd
        ? siteLocalToUtc(this.formData.sessionEnd, this.data.timezone)
        : undefined,
      engineHoursStart: this.formData.engineHoursStart ?? undefined,
      engineHoursEnd: this.formData.engineHoursEnd ?? undefined,
      drifterHoursStart: this.formData.drifterHoursStart ?? undefined,
      drifterHoursEnd: this.formData.drifterHoursEnd ?? undefined,
      totalPauseHours: this.totalPauseHours,
      totalDrilledLength: this.formData.totalDrilledLength ?? 0,
      numberOfHolesDrilled: this.data.session?.numberOfHolesDrilled ?? 0,
    };
    this.dialogRef.close(dto);
  }
}
